import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  TextField, 
  InputAdornment, 
  Box, 
  Button, 
  Paper, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  Chip,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../auth/useAuth';
import SearchIcon from '@mui/icons-material/Search';
import FlightIcon from '@mui/icons-material/Flight';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router-dom';

const FlightSeatingList = () => {
  const { api } = useAuth();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [seatToDelete, setSeatToDelete] = useState(null);

  const fetchFlights = useCallback(async (search, page, pageSize) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/flight-seating/all-flights', {
        params: {
          search,
          page: page + 1,
          limit: pageSize,
        },
      });
      
      if (response.data && response.data.success) {
        setFlights(response.data.data || []);
        
        if (response.data.pagination) {
          setTotalRows(response.data.pagination.totalItems);
        }
      }
    } catch (error) {
      console.error('Failed to fetch seats', error);
      setError('שגיאה בטעינת נתוני המושבים');
      setFlights([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const debouncedFetchFlights = useCallback(
    debounce((search, page, pageSize) => fetchFlights(search, page, pageSize), 300),
    [fetchFlights]
  );

  useEffect(() => {
    debouncedFetchFlights(searchTerm, paginationModel.page, paginationModel.pageSize);
  }, [debouncedFetchFlights, searchTerm, paginationModel]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleViewSeat = (seat) => {
    // ניווט לדף צפייה בטיסה
    navigate(`/admin/flight-seating/view/${seat.flightDate}/${seat.actualFlightNumber}/${seat.airline}`);
  };

  const handleDeleteSeat = (seat) => {
    setSeatToDelete(seat);
    setOpenDialog(true);
  };

  const confirmDeleteSeat = async () => {
    try {
      // כאן צריך להוסיף API למחיקת מושב בודד אם יש
      console.log('Deleting seat:', seatToDelete);
      
      setOpenDialog(false);
      setSeatToDelete(null);
      // רענון הרשימה
      debouncedFetchFlights(searchTerm, paginationModel.page, paginationModel.pageSize);
    } catch (error) {
      console.error('Failed to delete seat', error);
      setError('שגיאה במחיקת המושב');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'available': { label: 'פנוי', color: 'success' },
      'occupied': { label: 'תפוס', color: 'error' },
      'reserved': { label: 'שמור', color: 'warning' },
      'blocked': { label: 'חסום', color: 'default' },
      'selected': { label: 'נבחר', color: 'info' }
    };
    
    const config = statusConfig[status] || { label: status || 'לא ידוע', color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const columns = [
    {
      field: 'actions',
      headerName: 'פעולות',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewSeat(params.row)}
          >
            צפייה
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteSeat(params.row)}
          >
            מחק
          </Button>
        </Box>
      ),
    },
    { 
      field: 'flightId', 
      headerName: 'מזהה טיסה', 
      width: 180,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'flightDate', 
      headerName: 'תאריך טיסה', 
      width: 120,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'actualFlightNumber', 
      headerName: 'מספר טיסה', 
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlightIcon fontSize="small" color="action" />
          <span>{params.value || '-'}</span>
        </Box>
      )
    },
    { 
      field: 'airline', 
      headerName: 'חברת תעופה', 
      width: 100,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'seatNumber', 
      headerName: 'מספר מושב', 
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventSeatIcon fontSize="small" color="action" />
          <strong>{params.value || '-'}</strong>
        </Box>
      )
    },
    { 
      field: 'row', 
      headerName: 'שורה', 
      width: 70,
      type: 'number',
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'column', 
      headerName: 'עמודה', 
      width: 70,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'destination', 
      headerName: 'יעד', 
      width: 150,
      renderCell: (params) => params.value || 'לא צוין'
    },
    { 
      field: 'flightTime', 
      headerName: 'שעת טיסה', 
      width: 100,
      renderCell: (params) => params.value || 'לא צוינה'
    },
    { 
      field: 'direction', 
      headerName: 'כיוון', 
      width: 80,
      renderCell: (params) => {
        if (!params.value) return '-';
        if (params.value === 'H') return 'הלוך';
        if (params.value === 'R' || params.value === 'C') return 'חזור';
        return params.value;
      }
    },
    { 
      field: 'company', 
      headerName: 'חברה', 
      width: 100,
      renderCell: (params) => {
        const company = params.value || 'DEFAULT';
        const color = company === 'DERECH' ? 'primary' : 
                     company === 'NETIVIM' ? 'secondary' : 'default';
        return <Chip label={company} color={color} size="small" variant="outlined" />;
      }
    },
    { 
      field: 'journeyNumberDerech', 
      headerName: 'מס׳ נסיעה דרך', 
      width: 120,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'journeyNumberNetivim', 
      headerName: 'מס׳ נסיעה נתיבים', 
      width: 130,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'type', 
      headerName: 'סוג', 
      width: 60,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'typeDescription', 
      headerName: 'תיאור סוג', 
      width: 150,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'price', 
      headerName: 'מחיר', 
      width: 90,
      renderCell: (params) => {
        if (!params.value) return '-';
        return `₪${parseFloat(params.value).toFixed(2)}`;
      }
    },
    { 
      field: 'status', 
      headerName: 'סטטוס', 
      width: 100,
      renderCell: (params) => getStatusChip(params.value)
    },
    { 
      field: 'bookingReference', 
      headerName: 'מספר הזמנה', 
      width: 120,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'externalOrderNumber', 
      headerName: 'הזמנה חיצונית', 
      width: 130,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'isVisible', 
      headerName: 'גלוי', 
      width: 70,
      type: 'boolean',
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'כן' : 'לא'} 
          color={params.value ? 'success' : 'default'} 
          size="small" 
        />
      )
    },
    { 
      field: 'reservedAt', 
      headerName: 'תאריך הזמנה', 
      width: 150,
      renderCell: (params) => {
        if (!params.value) return '-';
        try {
          const date = new Date(params.value);
          return date.toLocaleDateString('he-IL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch {
          return '-';
        }
      }
    },
    { 
      field: 'createdAt', 
      headerName: 'תאריך יצירה', 
      width: 150,
      renderCell: (params) => {
        if (!params.value) return '-';
        try {
          const date = new Date(params.value);
          return date.toLocaleDateString('he-IL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch {
          return '-';
        }
      }
    },
    { 
      field: 'updatedAt', 
      headerName: 'עדכון אחרון', 
      width: 150,
      renderCell: (params) => {
        if (!params.value) return '-';
        try {
          const date = new Date(params.value);
          return date.toLocaleDateString('he-IL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch {
          return '-';
        }
      }
    }
  ];

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
          <EventSeatIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          ניהול מושבי טיסות
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="חיפוש לפי מספר טיסה, תאריך, חברת תעופה, יעד, מספר מושב או מספר הזמנה..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <strong>סה"כ מושבים במערכת:</strong> {totalRows.toLocaleString()}
          </Typography>
          <Typography variant="body2">
            <strong>מוצגים כעת:</strong> {flights.length}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ width: '100%', height: 600 }}>
        <DataGrid
          rows={flights}
          columns={columns}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalRows}
          pageSizeOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          getRowId={(row) => row.id}
          localeText={{
            noRowsLabel: 'אין מושבים להצגה',
            MuiTablePagination: {
              labelRowsPerPage: 'שורות לעמוד:',
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} מתוך ${count !== -1 ? count : `יותר מ-${to}`}`,
            },
          }}
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'action.hover',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"אישור מחיקת מושב"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            האם אתה בטוח שברצונך למחוק את המושב {seatToDelete?.seatNumber} 
            מטיסה {seatToDelete?.actualFlightNumber} בתאריך {seatToDelete?.flightDate}?
            <br />
            <br />
            <strong>יעד:</strong> {seatToDelete?.destination || 'לא צוין'}
            <br />
            <strong>חברת תעופה:</strong> {seatToDelete?.airline}
            <br />
            <strong>סטטוס:</strong> {seatToDelete?.status}
            <br />
            {seatToDelete?.bookingReference && (
              <>
                <strong>מספר הזמנה:</strong> {seatToDelete.bookingReference}
                <br />
              </>
            )}
            <br />
            <Alert severity="warning" sx={{ mt: 2 }}>
              פעולה זו תמחק את המושב לצמיתות ואינה ניתנת לביטול!
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            ביטול
          </Button>
          <Button onClick={confirmDeleteSeat} color="error" autoFocus variant="contained">
            מחק מושב
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FlightSeatingList;